import { type CSSProperties } from 'react'

export type AvaAvatarSize = 'sm' | 'md' | 'lg'

const SIZE_CLASSES: Record<AvaAvatarSize, string> = {
  sm: 'w-8 h-8',
  md: 'w-14 h-14',
  lg: 'w-20 h-20',
}

const DOT_SIZES: Record<AvaAvatarSize, string> = {
  sm: 'w-2.5 h-2.5',
  md: 'w-3 h-3',
  lg: 'w-4 h-4',
}

const DOT_POSITIONS: Record<AvaAvatarSize, CSSProperties> = {
  sm: { bottom: 0, right: 0 },
  md: { bottom: 0, right: 0 },
  lg: { bottom: 2, right: 2 },
}

export interface AvaAvatarProps {
  size?: AvaAvatarSize
  showOnlineDot?: boolean
  className?: string
}

/**
 * Ava's avatar image with an optional green "online" indicator dot.
 * Uses <base>Ava2.png (base-relative so it resolves under /admin/ + /control-center/).
 *
 * Size variants:
 *   sm  = 32px  (w-8 h-8)   — compact contexts: chat widgets, inline mentions
 *   md  = 56px  (w-14 h-14) — onboarding bubbles (standard)
 *   lg  = 80px  (w-20 h-20) — hero sections, support page
 */
export function AvaAvatar({
  size = 'md',
  showOnlineDot = true,
  className = '',
}: AvaAvatarProps) {
  return (
    <div className={`relative flex-shrink-0 ${SIZE_CLASSES[size]} ${className}`}>
      <img
        src={`${import.meta.env.BASE_URL}Ava2.png`}
        alt="Ava — your assistant"
        className="w-full h-full rounded-full object-cover"
      />
      {showOnlineDot && (
        <span
          className={`absolute ${DOT_SIZES[size]} bg-green-400 rounded-full border-2 border-white dark:border-slate-900`}
          style={DOT_POSITIONS[size]}
          aria-label="Online"
          role="img"
        />
      )}
    </div>
  )
}
